"use client";

import { useLayoutEffect, useState, type RefObject } from "react";

interface ViewportMaxHeightOptions {
    /** Teto de design (px): o painel nunca passa disso, mesmo com tela sobrando. */
    cap?: number;
    /** Respiro (px) entre o fim do painel e a borda inferior da tela. */
    bottomGap?: number;
    /** Piso (px): em tela muito baixa, melhor rolar a página do que um painel de 3 linhas. */
    min?: number;
    /** Mede só enquanto o elemento existe (ex.: dropdown aberto). */
    enabled?: boolean;
}

/**
 * Altura máxima de um painel ancorado no header até a borda inferior da tela.
 *
 * Os mega-menus nascem dentro do header sticky: o que passa da tela não é
 * alcançável rolando a página — rola junto. Daqui sai o espaço que realmente
 * sobra (topo do elemento → fim da viewport), e quem usa põe o conteúdo longo
 * numa coluna com `overflow-y-auto`. Reage a resize e scroll (o topo muda
 * quando o header não é sticky ou a barra de aviso some).
 */
export function useViewportMaxHeight<T extends HTMLElement>(
    ref: RefObject<T | null>,
    { cap = Number.POSITIVE_INFINITY, bottomGap = 16, min = 240, enabled = true }: ViewportMaxHeightOptions = {}
): number | undefined {
    const [maxHeight, setMaxHeight] = useState<number>();

    useLayoutEffect(() => {
        if (!enabled) return;
        const update = () => {
            const el = ref.current;
            if (!el) return;
            const available = window.innerHeight - el.getBoundingClientRect().top - bottomGap;
            setMaxHeight(Math.round(Math.min(cap, Math.max(min, available))));
        };

        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, { passive: true });
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update);
        };
    }, [ref, cap, bottomGap, min, enabled]);

    return enabled ? maxHeight : undefined;
}
